<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <base-widget
    icon="leaveAlt"
    icon-type="svg"
    class="emp-leave-chart"
    :empty="isEmpty"
    :empty-text="emptyText"
    :loading="isLoading"
    :title="$t('dashboard.employees_planned_leave')"
  >
    <template
      v-if="$can.update('dashboard_employees_on_leave_today_config')"
      #action
    >
      <oxd-icon
        class="orangehrm-leave-card-icon"
        name="gear-fill"
        @click="$emit('open-config')"
      />
    </template>
    <div
      v-for="leave in leaveList"
      :key="leave.listKey"
      class="orangehrm-leave-card"
    >
      <div class="orangehrm-leave-card-profile-image">
        <img
          alt="profile picture"
          class="employee-image"
          :src="`../pim/viewPhoto/empNumber/${leave.empNumber}`"
        />
      </div>
      <div class="orangehrm-leave-card-details">
        <oxd-text tag="p" class="orangehrm-leave-card-emp-name">
          {{ leave.empName }}
        </oxd-text>
        <oxd-text tag="p" class="orangehrm-leave-card-leave-details">
          {{ leave.leaveDetails }}
        </oxd-text>
      </div>
      <oxd-icon-button
        class="orangehrm-leave-card-details-btn"
        name="person-lines-fill"
        display-type="secondary"
        :title="$t('leave.view_pim_info')"
        @click="onViewEmployeeDetails(leave)"
      />
    </div>
  </base-widget>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {freshDate, formatDate, parseDate} from '@ohrm/core/util/helper/datefns';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useBuzzEmployeeMentionDialog from '@/orangehrmBuzzPlugin/util/composable/useBuzzEmployeeMentionDialog';
import {formatDashboardShiftPermissionLine} from '@/orangehrmLeavePlugin/util/shiftPermissionExpectedTime';
import {OxdIcon, OxdIconButton} from '@ohrm/oxd';

export default {
  name: 'EmployeesPlannedLeaveWidget',

  components: {
    'oxd-icon': OxdIcon,
    'oxd-icon-button': OxdIconButton,
    'base-widget': BaseWidget,
  },
  emits: ['open-config'],

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/dashboard/employees/planned-leaves',
    );
    const {$tEmpName} = useEmployeeNameTranslate();
    const {jsDateFormat} = useDateFormat();
    const {openEmployeeMention} = useBuzzEmployeeMentionDialog();

    return {
      http,
      tEmpName: $tEmpName,
      jsDateFormat,
      openEmployeeMention,
    };
  },

  data() {
    return {
      leaveList: [],
      isLoading: false,
    };
  },

  computed: {
    isEmpty() {
      return this.leaveList.length === 0;
    },
    emptyText() {
      return this.$t('dashboard.no_employees_planned_leave');
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .getAll({
        date: formatDate(freshDate(), 'yyyy-MM-dd'),
      })
      .then((response) => {
        const {data} = response.data;
        this.leaveList = data.map((item) => {
          const {employee, startDate, lengthDays, source} = item;
          const formattedStartDate = formatDate(
            parseDate(startDate),
            this.jsDateFormat,
          );
          let leaveDetails = this.$t(
            'dashboard.planned_leave_duration_details',
            {
              startDate: formattedStartDate,
              duration: lengthDays,
            },
          );

          if (source === 'early_leave' || source === 'late_arrival') {
            leaveDetails =
              formatDashboardShiftPermissionLine(item, {
                formattedDate: formattedStartDate,
                labelEarly: this.$t('dashboard.shift_permission_early'),
                labelLate: this.$t('dashboard.shift_permission_late'),
              }) ?? leaveDetails;
          }

          return {
            id: item.id,
            listKey: `${source || 'leave'}-${item.id}`,
            leaveDetails,
            empNumber: employee.empNumber,
            pic: item.pic ?? null,
            empName: this.tEmpName(employee, {
              includeMiddle: true,
              excludePastEmpTag: false,
            }),
          };
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onViewEmployeeDetails(leave) {
      if (!leave?.empNumber) {
        return;
      }
      this.openEmployeeMention(leave.empNumber, {
        assignedPic: leave.pic,
      });
    },
  },
};
</script>

<style src="./employee-on-leave-widget.scss" lang="scss" scoped></style>
